import React from "react";

import { Button } from "@/client/components/ui/index";
import { useEditableField } from "@/client/hooks/useEditableField";

/**
 * Section with a label, edit toggle, and a textarea that shows/hides on edit.
 * Manages its own edit state via useEditableField.
 */
export function EditableTextField({
  label,
  value: original,
  onSave,
  saving = false,
  placeholder,
  rows = 3,
  mono = false,
  emptyText = "Sin contenido",
  headerExtra,
  viewContent,
}: {
  label: string;
  value: string;
  onSave: (value: string) => void;
  saving?: boolean;
  placeholder?: string;
  rows?: number;
  mono?: boolean;
  emptyText?: string;
  /** Extra buttons shown next to the edit link when not editing */
  headerExtra?: React.ReactNode;
  /** Custom view-mode content (replaces default text display) */
  viewContent?: React.ReactNode;
}) {
  const field = useEditableField(original);

  function handleSave() {
    onSave(field.value);
    field.done();
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-medium text-term-text-dim uppercase tracking-wide">
          {label}
        </span>
        {!field.editing && (
          <>
            <button
              onClick={field.startEditing}
              className="text-sm text-term-accent cursor-pointer hover:underline"
            >
              {original ? "Editar" : "Agregar"}
            </button>
            {headerExtra}
          </>
        )}
      </div>
      {field.editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            value={field.value}
            onChange={(e) => field.setValue(e.target.value)}
            rows={rows}
            autoFocus
            className={`w-full px-3 py-2 ${mono ? "text-sm font-mono" : "text-sm"} bg-term-bg border border-term-border rounded-lg text-term-text focus:border-term-accent-dim focus:outline-none ${mono ? "resize-y" : "resize-none"}`}
            placeholder={placeholder}
          />
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button variant="ghost" size="sm" onClick={field.cancel}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : viewContent ? (
        viewContent
      ) : (
        <p className="text-sm text-term-text-dim selectable">
          {original || <span className="italic text-term-text-muted">{emptyText}</span>}
        </p>
      )}
    </div>
  );
}
